/**
 * VpnWarningBanner - Элегантное предупреждение о VPN и региональных ограничениях
 * 
 * Показывается только если:
 * - VPN детектирован
 * - Есть ограничения по региону
 * 
 * Не блокирует, информирует и предлагает альтернативы
 * 
 * CANON v1.3.0 Compliant:
 * - Semantic tokens only
 * - 240ms canonical motion
 * - A11y: WCAG 2.2 AA
 * 
 * Owner: @i (Auth Flow Optimization)
 * Created: 2025-11-05
 */

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import React from 'react';

interface VpnWarningBannerProps {
  show: boolean;
  vpnDetected: boolean;
  hasRestrictions: boolean;
  restrictionMessage?: string;
  onViewCoverage?: () => void;
  className?: string;
}

export const VpnWarningBanner: React.FC<VpnWarningBannerProps> = ({
  show,
  vpnDetected,
  hasRestrictions,
  restrictionMessage,
  onViewCoverage,
  className
}) => {
  // Don't show if neither VPN nor restrictions
  if (!show || (!vpnDetected && !hasRestrictions)) {
    return null;
  }

  // Different messages based on scenario
  const getAlertConfig = () => {
    if (vpnDetected && hasRestrictions) {
      return {
        variant: 'default' as const,
        title: 'VPN Detected with Regional Restrictions',
        description: `We noticed you might be using a VPN. ${restrictionMessage || 'Some services may not be fully available in your region.'}`,
        bgColor: cn(semanticTokens.warning.bg, semanticTokens.warning.border)
      };
    }

    if (vpnDetected) {
      return {
        variant: 'default' as const,
        title: 'VPN Detected',
        description: 'We noticed you might be using a VPN. This is completely fine! For faster verification, we recommend connecting without VPN during KYC onboarding.',
        bgColor: cn(semanticTokens.info.bg, semanticTokens.info.border)
      };
    }

    if (hasRestrictions) {
      return {
        variant: 'default' as const,
        title: 'Service Availability Notice',
        description: restrictionMessage || 'Some services may require additional verification in your region.',
        bgColor: cn(semanticTokens.warning.bg, semanticTokens.warning.border)
      };
    }

    return null;
  };

  const config = getAlertConfig();
  if (!config) return null;

  return (
    <Alert
      className={cn(
        config.bgColor,
        "animate-in fade-in-50 slide-in-from-top-4 duration-240",
        className
      )}
      role="alert"
      aria-live="polite"
    >
      <SFIcon icon={sfIcons.info} className="h-4 w-4" />
      <AlertTitle className="text-sm font-medium">
        {config.title}
      </AlertTitle>
      <AlertDescription className="text-sm mt-1">
        {config.description}
      </AlertDescription>

      {/* Call to action - View Coverage */}
      {onViewCoverage && (
        <Button
          variant="link"
          size="sm"
          onClick={onViewCoverage}
          className={cn("p-0 h-auto mt-2", semanticTokens.accent.fg, "hover:text-blue-700")}
        >
          View service coverage by region
          <SFIcon icon={sfIcons.externalLink} className="w-3 h-3 ml-1" />
        </Button>
      )}
    </Alert>
  );
};

